// "Share my grade" for the free report card: draws the week's grades as a 1200×630 image
// (dollar amounts only if the trader chooses) to download or share.
import { useState } from 'react';
import { formatMoney } from './pnl';
import { useI18n } from './i18n';
import { asQuarter } from './weeklyGrades';

const COLORS = { A: '#2fe39a', B: '#1fd7ef', C: '#f5b941', D: '#f59e5b', F: '#ff5d73' };
const colorOf = (grade) => COLORS[grade[0]] || '#f1f6fb';

// Week or month wording; a quarter uses the month's in quarter words.
const wording = (card, t) => (weekText, monthText) => (card.period === 'quarter' ? asQuarter(t(monthText)) : card.period === 'month' ? t(monthText) : t(weekText));

function draw(card, { week, showMoney, t }) {
  const w = wording(card, t);
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const c = canvas.getContext('2d');
  c.fillStyle = '#040d1f';
  c.fillRect(0, 0, 1200, 630);
  c.strokeStyle = '#15506a';
  c.lineWidth = 2;
  c.strokeRect(24, 24, 1152, 582);
  const font = (weight, size, family = 'Inter, Arial, sans-serif') => `${weight} ${size}px ${family}`;

  c.fillStyle = '#f1f6fb';
  c.font = font(800, 30);
  c.fillText('MARKET MATRIX', 64, 92);
  c.fillStyle = '#1fd7ef';
  c.font = font(700, 18);
  c.fillText(w('WEEKLY REPORT CARD', 'MONTHLY REPORT CARD').toUpperCase(), 64, 122);
  c.fillStyle = '#93a7bd';
  c.font = font(500, 22);
  c.fillText(week, 64, 160);

  // The overall grade.
  c.fillStyle = '#071d33';
  c.fillRect(64, 196, 300, 300);
  c.strokeStyle = colorOf(card.grade);
  c.lineWidth = 6;
  c.strokeRect(64, 196, 300, 300);
  c.fillStyle = colorOf(card.grade);
  c.font = font(700, 150, 'JetBrains Mono, Menlo, monospace');
  c.textAlign = 'center';
  c.fillText(card.grade, 214, 400);
  c.fillStyle = '#93a7bd';
  c.font = font(800, 20);
  c.fillText(t('Overall').toUpperCase(), 214, 460);
  c.textAlign = 'left';

  // The subjects.
  card.subjects.forEach((s, i) => {
    const y = 214 + i * 74;
    c.fillStyle = '#071d33';
    c.fillRect(420, y, 700, 60);
    c.fillStyle = colorOf(s.grade);
    c.font = font(700, 34, 'JetBrains Mono, Menlo, monospace');
    c.fillText(s.grade, 444, y + 42);
    c.fillStyle = '#f1f6fb';
    c.font = font(700, 26);
    c.fillText(s.label, 540, y + 40);
  });

  c.fillStyle = '#c4d2e0';
  c.font = font(600, 22);
  const tail = showMoney ? `${t('{n} trades', { n: card.count })} · ${formatMoney(card.net)}` : t('{n} trades', { n: card.count });
  c.fillText(tail, 64, 548);
  c.fillStyle = '#1fd7ef';
  c.font = font(700, 22);
  c.textAlign = 'right';
  c.fillText(t('Grade your trades free: marketmatrixhq.com/report-card'), 1136, 568);
  return canvas;
}

export default function GradeShare({ card, week }) {
  const { t } = useI18n();
  const [showMoney, setShowMoney] = useState(false);
  const [busy, setBusy] = useState(false);

  async function share() {
    setBusy(true);
    try {
      const canvas = draw(card, { week, showMoney, t });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'market-matrix-report-card.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: wording(card, t)('My weekly trading report card', 'My monthly trading report card'), text: t('Grade your trades free: marketmatrixhq.com/report-card') }).catch(() => {});
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grade-share">
      <div>
        <strong>{t('Share your grade')}</strong>
        <span>{wording(card, t)('An image of this week’s grades for X, Discord or Instagram.', 'An image of this month’s grades for X, Discord or Instagram.')}</span>
      </div>
      <label className="goal-reminder-email">
        <input type="checkbox" checked={showMoney} onChange={(e) => setShowMoney(e.target.checked)} />
        {t('Show my P&L on it')}
      </label>
      <button type="button" className="small" onClick={share} disabled={busy}>{busy ? t('Making it…') : t('Share my grade')}</button>
    </div>
  );
}
